import React, { useCallback, useEffect, useState } from "react";

/**
 * Substituto "drop-in" de react-facebook-login (render-props) com suporte a
 * Facebook Login for Business via `configId` (FB.login({ config_id })).
 *
 * Props: appId, configId, scope (fallback quando não há configId), callback, render.
 * O callback recebe { status, accessToken, id, userID } — o mesmo formato que os
 * handlers do CRM (responseFacebook / responseInstagram) já consomem.
 */
const SDK_ID = "facebook-jssdk";
const API_VERSION = process.env.REACT_APP_FACEBOOK_API_VERSION || "v22.0";

let sdkPromise = null;

function loadFacebookSdk(appId) {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const init = () => {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: API_VERSION,
      });
      resolve(window.FB);
    };

    if (window.FB) {
      init();
      return;
    }

    window.fbAsyncInit = init;

    if (!document.getElementById(SDK_ID)) {
      const js = document.createElement("script");
      js.id = SDK_ID;
      js.async = true;
      js.defer = true;
      js.src = "https://connect.facebook.net/pt_BR/sdk.js";
      js.onerror = () => {
        sdkPromise = null;
        reject(new Error("Não foi possível carregar o SDK do Facebook"));
      };
      document.body.appendChild(js);
    }
  });

  return sdkPromise;
}

const FacebookLoginConfig = ({
  appId,
  configId,
  scope,
  callback,
  render,
  isDisabled = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Pré-carrega o SDK: FB.login precisa rodar direto no clique (anti pop-up block).
    if (appId) loadFacebookSdk(appId).catch(() => {});
  }, [appId]);

  const handleResponse = useCallback(
    (response) => {
      setIsProcessing(false);
      const auth = response && response.authResponse;
      if (auth && auth.accessToken) {
        callback({
          status: "connected",
          accessToken: auth.accessToken,
          id: auth.userID,
          userID: auth.userID,
        });
      } else {
        callback({ status: "unknown" });
      }
    },
    [callback]
  );

  const onClick = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (isDisabled || isProcessing) return;

      const options = configId
        ? { config_id: configId }
        : { scope, return_scopes: true, auth_type: "rerequest" };

      if (window.FB) {
        setIsProcessing(true);
        window.FB.login(handleResponse, options);
        return;
      }

      // SDK ainda não carregou: tenta carregar e abrir em seguida.
      setIsProcessing(true);
      loadFacebookSdk(appId)
        .then((FB) => FB.login(handleResponse, options))
        .catch(() => {
          setIsProcessing(false);
          callback({ status: "unknown" });
        });
    },
    [appId, configId, scope, callback, handleResponse, isDisabled, isProcessing]
  );

  return render({ onClick, isProcessing, isDisabled });
};

export default FacebookLoginConfig;
